import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import { PhotoCamera, Mic, Replay } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { paths } from "../routes/path";

const CameraPermissionDialog = ({
  open,
  onClose,
  questions,
  candidate,
  position,
  thinkingTime,
  maxAnswerLength,
}) => {
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [micTested, setMicTested] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const videoRef = useRef(null);
  const recordedVideoRef = useRef(null);
  const questionVideoRef = useRef(null);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const mediaRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const chunksRef = useRef([]);
  const navigate = useNavigate();
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [submittedQuestions, setSubmittedQuestions] = useState(new Set());
  const [remainingRetakes, setRemainingRetakes] = useState(0);
  const [retakesPerQuestion, setRetakesPerQuestion] = useState({});
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);

  // Timing states
  const [thinkingTimer, setThinkingTimer] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const [questionVideoFinished, setQuestionVideoFinished] = useState(false);

  // Parse timing values
  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 15;
    const [value, unit] = timeString.split(" ");
    const numericValue = parseInt(value);
    return unit.toLowerCase().includes("minute") ? numericValue * 60 : numericValue;
  };

  const thinkingTimeSeconds = parseTimeToSeconds(thinkingTime);
  const maxAnswerLengthSeconds = parseTimeToSeconds(maxAnswerLength);

  // Parse retake count
  useEffect(() => {
    if (position && position.question_retakes) {
      const retakeCount = parseInt(position.question_retakes);
      setRemainingRetakes(retakeCount);
      const initialRetakes = {};
      questions.forEach((_, index) => {
        initialRetakes[index] = retakeCount;
      });
      setRetakesPerQuestion(initialRetakes);
    }
  }, [position, questions]);

  // Thinking timer effect - now only starts when question video is finished
  useEffect(() => {
    let thinkingInterval;

    if (interviewStarted && !recording && !showReviewScreen && mediaStream && questionVideoFinished) {
      setThinkingTimer(thinkingTimeSeconds);
      thinkingInterval = setInterval(() => {
        setThinkingTimer((prev) => {
          if (prev <= 1) {
            if (!recording && mediaStream) {
              startRecording(); // Auto-start recording when thinking time expires
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(thinkingInterval);
  }, [interviewStarted, currentQuestionIndex, recording, showReviewScreen, mediaStream, thinkingTimeSeconds, questionVideoFinished]);

  // Recording timer effect
  useEffect(() => {
    let recordingInterval;
    if (recording && recordingTimer < maxAnswerLengthSeconds) {
      recordingInterval = setInterval(() => {
        setRecordingTimer((prev) => prev + 1);
      }, 1000);
    } else if (recording && recordingTimer >= maxAnswerLengthSeconds) {
      stopRecording();
    }
    return () => clearInterval(recordingInterval);
  }, [recording, recordingTimer, maxAnswerLengthSeconds]);

  // Reset thinking timer and initialize camera when question changes
  useEffect(() => {
    if (interviewStarted && !showReviewScreen) {
      setQuestionVideoFinished(false); // Reset video finished state for new question
      setThinkingTimer(0); // Reset thinking timer
      setRecording(false);
      setRecordingTimer(0);
      initializeCamera(); // Initialize camera when question changes
    }
  }, [currentQuestionIndex, interviewStarted, showReviewScreen, thinkingTimeSeconds]);

  // Handle question video ended event
  const handleQuestionVideoEnded = () => {
    setQuestionVideoFinished(true);
  };

  // Handle question video play event (in case user seeks back)
  const handleQuestionVideoPlay = () => {
    // If video is played again, reset the finished state
    setQuestionVideoFinished(false);
  };

  const requestCameraAccess = async () => {
    if (!selectedCamera || !selectedMic) {
      alert("Please select camera and microphone first.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedCamera } },
        audio: { deviceId: { exact: selectedMic } },
      });

      setMediaStream(stream);
      setPermissionGranted(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((error) => {
            console.error("Error playing video:", error);
          });
        }
      }, 100);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      }

      // Initialize audio context for microphone testing
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      // Start audio level detection
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const detectAudio = () => {
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        setAudioLevel(average);

        if (average > 30) {
          setMicTested(true);
        }

        if (!micTested) {
          requestAnimationFrame(detectAudio);
        }
      };
      detectAudio();
    } catch (error) {
      console.error("Permission denied", error);
      alert("Camera and microphone access is required to proceed.");
      setPermissionGranted(false);
    }
  };

  const initializeCamera = async () => {
    if (!selectedCamera || !selectedMic) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedCamera } },
        audio: { deviceId: { exact: selectedMic } },
      });

      setMediaStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      }
    } catch (error) {
      console.error("Error initializing camera:", error);
      alert("Failed to initialize camera. Please check your permissions.");
    }
  };

  useEffect(() => {
    const getDevices = async () => {
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const cameras = allDevices.filter((device) => device.kind === "videoinput");
        const mics = allDevices.filter((device) => device.kind === "audioinput");
        setDevices({ cameras, mics });
        if (cameras[0]) setSelectedCamera(cameras[0].deviceId);
        if (mics[0]) setSelectedMic(mics[0].deviceId);
      } catch (err) {
        console.error("Error fetching devices:", err);
      }
    };

    if (open) {
      getDevices();
    }
  }, [open]);

  useEffect(() => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    }
  }, [mediaStream]);

  const stopMediaStream = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const startRecording = () => {
    if (!mediaStream) {
      alert("Camera and microphone not initialized! Please try again.");
      initializeCamera(); // Attempt to reinitialize
      return;
    }

    setRecordingTimer(0);
    const options = { mimeType: "video/webm; codecs=vp9" };
    mediaRecorderRef.current = new MediaRecorder(mediaStream, options);
    chunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setVideoBlob(blob);
      const url = URL.createObjectURL(blob);
      setRecordedVideoUrl(url);
      setShowReviewScreen(true);
      stopMediaStream();
    };

    mediaRecorderRef.current.start();
    setRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const retakeRecording = async () => {
    const currentRetakes = retakesPerQuestion[currentQuestionIndex];
    if (currentRetakes <= 0) return;

    setRetakesPerQuestion((prev) => ({
      ...prev,
      [currentQuestionIndex]: prev[currentQuestionIndex] - 1,
    }));

    setShowReviewScreen(false);
    setRecordedVideoUrl(null);
    setVideoBlob(null);
    setRecordingTimer(0);
    setQuestionVideoFinished(false); // Reset video finished state
    setThinkingTimer(0); // Reset thinking timer

    if (recordedVideoUrl) {
      URL.revokeObjectURL(recordedVideoUrl);
    }

    await initializeCamera();
  };

  const handleSubmit = async () => {
    if (!videoBlob) {
      alert("No video recorded!");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("url_id", candidate);
    formData.append("video", videoBlob, "recording.webm");
    formData.append("question_id", questions[currentQuestionIndex]?.id);

    try {
      const response = await axios.post(
        "https://api.a2ajobs.com/api/candidate-video-upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        }
      );
      setSubmitting(false);

      setSubmittedQuestions((prev) => new Set([...prev, currentQuestionIndex]));

      setRetakesPerQuestion((prev) => ({
        ...prev,
        [currentQuestionIndex]: parseInt(position.question_retakes),
      }));

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setShowReviewScreen(false);
        setRecordedVideoUrl(null);
        setVideoBlob(null);
        setRecordingTimer(0);
        setQuestionVideoFinished(false); // Reset for next question
        setThinkingTimer(0);
        await initializeCamera();
      } else {
        const sessionData = {
          position: position,
          candidate: candidate,
        };
        sessionStorage.setItem("positionData", JSON.stringify(sessionData));
        navigate(paths.finishInterview);
      }
    } catch (error) {
      console.error("Upload Error:", error);
      setSubmitting(false);
      alert("Failed to upload video.");
    }
  };

  const handleNextQuestion = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowReviewScreen(false);
      setRecordedVideoUrl(null);
      setVideoBlob(null);
      setRecordingTimer(0);
      setQuestionVideoFinished(false); // Reset for next question
      setThinkingTimer(0);
      await initializeCamera();
    }
  };

  const handlePreviousQuestion = async () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowReviewScreen(false);
      setRecordedVideoUrl(null);
      setVideoBlob(null);
      setRecordingTimer(0);
      setQuestionVideoFinished(false); // Reset for previous question
      setThinkingTimer(0);
      await initializeCamera();
    }
  };

  useEffect(() => {
    if (!open) {
      stopMediaStream();
      setPermissionGranted(false);
      setInterviewStarted(false);
      setCurrentQuestionIndex(0);
      setRecording(false);
      setShowReviewScreen(false);
      setRecordedVideoUrl(null);
      setVideoBlob(null);
      setQuestionVideoFinished(false);
      setThinkingTimer(0);
      setRecordingTimer(0);
      setSubmittedQuestions(new Set());
      setMicTested(false);
      setAudioLevel(0);

      if (position && position.question_retakes) {
        const retakeCount = parseInt(position.question_retakes);
        const resetRetakes = {};
        questions.forEach((_, index) => {
          resetRetakes[index] = retakeCount;
        });
        setRetakesPerQuestion(resetRetakes);
      }
    }
  }, [open, position, questions, thinkingTimeSeconds]);

  useEffect(() => {
    return () => {
      if (recordedVideoUrl) {
        URL.revokeObjectURL(recordedVideoUrl);
      }
    };
  }, [recordedVideoUrl]);

  // Initialize camera when interview starts
  useEffect(() => {
    if (interviewStarted && !mediaStream) {
      initializeCamera();
    }
  }, [interviewStarted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const currentQuestion = questions[currentQuestionIndex];
  const currentRetakes = retakesPerQuestion[currentQuestionIndex] || 0;

  return (
    <>
      {!permissionGranted && (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <Stack p={{ md: 5, xs: 0 }}>
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <Stack sx={{ fontWeight: 600, textAlign: "center" }}>
              <Box display="flex" justifyContent="center" gap={2} my={2}>
                <PhotoCamera sx={{ fontSize: 48, color: "primary.main" }} />
                <Mic sx={{ fontSize: 48, color: "primary.main" }} />
              </Box>
              Camera and Microphone Permission Required
            </Stack>
            <DialogContent sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Please allow access to your camera and microphone for the interview to proceed.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={requestCameraAccess}
                sx={{ textTransform: "none" }}
              >
                Allow Access
              </Button>
            </DialogActions>
          </Stack>
        </Dialog>
      )}

      {permissionGranted && (
        <Dialog
          open={open}
          onClose={() => {
            stopMediaStream();
            onClose();
          }}
          maxWidth="lg"
          fullWidth
          sx={{ "& .MuiDialog-paper": { maxHeight: "90vh" } }}
        >
          <DialogContent>
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>

            {!interviewStarted ? (
              <Stack sx={{ flexDirection: { xs: "column", md: "row" }, gap: 2, mt: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "black",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "auto",
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: 300,
                      bgcolor: "#333",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    {selectedCamera ? (
                      <video
                        ref={videoRef}
                        autoPlay
                        muted
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                          transform: "scaleX(-1)",
                        }}
                      />
                    ) : (
                      <Typography color="white">Camera preview will appear here</Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="white" textAlign="center">
                    Ensure your image is clear
                  </Typography>
                  <Box
                    sx={{
                      width: "80%",
                      height: 5,
                      bgcolor: "green",
                      mt: 1,
                      borderRadius: 2,
                      transform: `scaleX(${audioLevel / 100})`,
                      transformOrigin: "left",
                      transition: "transform 0.1s ease-in-out",
                    }}
                  />
                </Box>
                <Box sx={{ p: { md: 3, xs: 0 }, width: { md: "50%", xs: "100%" } }}>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Audio & Video Test
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    Camera
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={selectedCamera}
                    onChange={(e) => setSelectedCamera(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    {devices.cameras.map((cam) => (
                      <MenuItem key={cam.deviceId} value={cam.deviceId}>
                        {cam.label || `Camera ${cam.deviceId}`}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography variant="body1" fontWeight="bold">
                    Microphone
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={selectedMic}
                    onChange={(e) => setSelectedMic(e.target.value)}
                    sx={{ mb: 3 }}
                  >
                    {devices.mics.map((mic) => (
                      <MenuItem key={mic.deviceId} value={mic.deviceId}>
                        {mic.label || `Microphone ${mic.deviceId}`}
                      </MenuItem>
                    ))}
                  </Select>

                  <Box mt={1} p={2} bgcolor="#E6F4EA" borderRadius={2}>
                    <Typography color="green" fontWeight="bold" fontSize={12}>
                      {micTested
                        ? "Microphone check successful! Please proceed when ready."
                        : "Please speak to test your microphone"}
                    </Typography>
                  </Box>
                  <Stack alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        px: 4,
                        mt: 2,
                        mb: 1,
                        textTransform: "none",
                        bgcolor: "#1666EB",
                        opacity: micTested ? 1 : 0.5,
                        "&:hover": {
                          bgcolor: micTested ? "#1356c7" : "#1666EB",
                        },
                      }}
                      onClick={() => {
                        setInterviewStarted(true);
                      }}
                      disabled={!micTested}
                    >
                      Start Interview
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            ) : showReviewScreen ? (
              <Box>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Review Your Answer for Question {currentQuestionIndex + 1}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                      Question Video
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        height: 300,
                        bgcolor: "#000",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <video
                        ref={questionVideoRef}
                        src={currentQuestion?.media?.[0]?.original_url || currentQuestion?.videoUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: 8,
                        }}
                        controls
                      />
                    </Box>
                    <Typography variant="body2" mt={1} fontWeight="bold">
                      {currentQuestion?.question_title}
                    </Typography>
                    <Typography variant="body2">{currentQuestion?.full_question}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                      Your Recorded Answer
                    </Typography>
                    <Box
                      sx={{
                        height: 300,
                        bgcolor: "#000",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <video
                        ref={recordedVideoRef}
                        src={recordedVideoUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: 8,
                        }}
                        controls
                      />
                    </Box>
                    <Typography variant="body2" mt={1} textAlign="center">
                      Duration: {formatTime(recordingTimer)}
                    </Typography>
                    <Typography variant="body2" textAlign="center" color="text.secondary">
                      Retakes remaining: {currentRetakes}
                    </Typography>
                  </Grid>
                </Grid>

                <DialogActions sx={{ justifyContent: "center", mt: 2, gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Replay />}
                    onClick={retakeRecording}
                    disabled={submitting || currentRetakes <= 0}
                  >
                    {currentRetakes > 0 ? `Retake (${currentRetakes} left)` : "No Retakes Left"}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{ bgcolor: "#1666EB" }}
                  >
                    {submitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : currentQuestionIndex < questions.length - 1 ? (
                      "Submit & Next Question"
                    ) : (
                      "Submit & Finish"
                    )}
                  </Button>
                </DialogActions>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </Typography>

                {/* Thinking Time Display - Only show when video is finished */}
                {questionVideoFinished && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: "#e8f5e8",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      Thinking Time: {thinkingTimer}s remaining
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                      Question Video
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        height: 300,
                        bgcolor: "#000",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <video
                        ref={questionVideoRef}
                        src={currentQuestion?.media?.[0]?.original_url || currentQuestion?.videoUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: 8,
                        }}
                        controls
                        onEnded={handleQuestionVideoEnded}
                        onPlay={handleQuestionVideoPlay}
                      />
                      {!questionVideoFinished && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            backgroundColor: "rgba(0,0,0,0.7)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          <Typography variant="body2">
                            Watch the question video first
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" mt={1} fontWeight="bold">
                      {currentQuestion?.question_title}
                    </Typography>
                    <Typography variant="body2">{currentQuestion?.full_question}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                      Your Camera
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        height: 300,
                        bgcolor: "#000",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <video
                        ref={videoRef}
                        autoPlay
                        muted
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                          transform: "scaleX(-1)",
                        }}
                      />
                      {recording && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(255,0,0,0.7)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "white" }} />
                          <Typography variant="body2">
                            {formatTime(recordingTimer)} / {formatTime(maxAnswerLengthSeconds)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {!recording ? (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{
                            bgcolor: "#1666EB",
                            "&:hover": {
                              bgcolor: "#1356c7",
                            },
                            opacity: questionVideoFinished ? 1 : 0.5,
                          }}
                          onClick={startRecording}
                          disabled={!mediaStream || submittedQuestions.has(currentQuestionIndex) || !questionVideoFinished}
                        >
                          {submittedQuestions.has(currentQuestionIndex)
                            ? "Already Submitted"
                            : questionVideoFinished
                              ? "Record Answer"
                              : "Watch Question Video First"}
                        </Button>
                        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                          {questionVideoFinished
                            ? "Click to start recording your answer"
                            : "Please watch the question video first"}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Button
                          variant="contained"
                          color="error"
                          fullWidth
                          onClick={stopRecording}
                        >
                          Stop Recording ({maxAnswerLengthSeconds - recordingTimer}s left)
                        </Button>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Time remaining: {maxAnswerLengthSeconds - recordingTimer}s
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <DialogActions sx={{ justifyContent: "space-between", mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0 || submittedQuestions.has(currentQuestionIndex - 1)}
                  >
                    Previous Question
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleNextQuestion}
                    disabled={currentQuestionIndex === questions.length - 1}
                  >
                    Next Question
                  </Button>
                </DialogActions>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default CameraPermissionDialog;